import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Shield,
  Users,
  User,
  Save,
  Eye,
  Edit2,
  Trash2,
  Settings,
  Check,
  X,
  RefreshCw,
  Loader2,
  ChevronRight,
  Lock,
  Unlock,
  Building,
  UserCheck,
  Globe,
  Target,
  Plus,
  FileText,
  Play,
  MessageSquare,
  Download,
  ArrowRightLeft,
  CheckCircle
} from 'lucide-react';

const ACCIONES = [
  { key: 'ver', label: 'Ver', icon: Eye, color: 'text-blue-600' },
  { key: 'crear', label: 'Crear', icon: Plus, color: 'text-green-600' },
  { key: 'editar', label: 'Editar', icon: Edit2, color: 'text-amber-600' },
  { key: 'eliminar', label: 'Eliminar', icon: Trash2, color: 'text-red-600' },
  { key: 'aprobar', label: 'Aprobar', icon: CheckCircle, color: 'text-purple-600' },
  { key: 'exportar', label: 'Exportar', icon: Download, color: 'text-cyan-600' },
  { key: 'ejecutar', label: 'Ejecutar', icon: Play, color: 'text-emerald-600' },
  { key: 'reasignar', label: 'Reasignar', icon: ArrowRightLeft, color: 'text-orange-600' },
  { key: 'comentar', label: 'Comentar', icon: MessageSquare, color: 'text-slate-600' },
];

const TIPOS_ALCANCE = [
  { key: 'propio', label: 'Solo propio', icon: User, descripcion: 'Solo registros creados por el usuario' },
  { key: 'grupo', label: 'Mi grupo', icon: Users, descripcion: 'Registros del grupo del usuario' },
  { key: 'grupos_supervisados', label: 'Grupos supervisados', icon: UserCheck, descripcion: 'Su grupo + grupos que supervisa' },
  { key: 'todo', label: 'Todos', icon: Globe, descripcion: 'Sin restricción de alcance' },
];

// Notas contextuales por sección: explican qué habilita una acción cuando su efecto no es obvio.
const NOTAS_SECCION = {
  cobranzas: 'La acción "Eliminar" habilita "Sacar de Gestión" (excluir/restaurar deudas) y ver el tab "Resueltas por fuera".',
};

// Permisos propios de una sección, además de las acciones comunes.
const ACCIONES_EXTRA_SECCION = {
  'kpis-atencion': [{ key: 'informe_semanal', label: 'Exportar informe semanal' }],
};

const notaSeccion = (sec) => {
  const k = sec?.key || '';
  if (k === 'cobranzas' || (sec?.ruta || '').includes('/cobranzas') || sec?.nombre === 'Cobranzas') {
    return NOTAS_SECCION.cobranzas;
  }
  return null;
};

const PermisosConfigV2 = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Data
  const [grupos, setGrupos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [modulos, setModulos] = useState([]);
  
  // Selección actual
  const [selectedGrupo, setSelectedGrupo] = useState(null);
  const [selectedModulo, setSelectedModulo] = useState(null);
  
  // Permisos en edición
  const [permisosEditando, setPermisosEditando] = useState({});
  // Estructura: { seccionId: { acciones: {ver: true, ...}, alcance: {tipo: 'grupo', ...} } }
  
  // Tab activo
  const [activeTab, setActiveTab] = useState('grupos');
  
  // Dialog para override de usuario
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [selectedUsuario, setSelectedUsuario] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedGrupo && selectedModulo) {
      loadPermisosGrupo();
    }
  }, [selectedGrupo, selectedModulo]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [gruposRes, usuariosRes, modulosRes] = await Promise.all([
        api.get('/groups'),
        api.get('/users'),
        api.get('/modules')
      ]);
      
      setGrupos(gruposRes.data || []);
      setUsuarios(usuariosRes.data || []);
      setModulos(modulosRes.data || []);
      
      // Seleccionar primero por defecto
      if (gruposRes.data?.length > 0) {
        setSelectedGrupo(gruposRes.data[0]);
      }
      if (modulosRes.data?.length > 0) {
        setSelectedModulo(modulosRes.data[0]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadPermisosGrupo = async () => {
    if (!selectedGrupo || !selectedModulo) return;
    
    try {
      const res = await api.get(`/permisos-v2/grupo/${selectedGrupo.id}`, {
        params: { modulo_id: selectedModulo.id }
      });
      
      // Convertir a estructura de edición
      const permisos = {};
      
      // Inicializar todas las secciones del módulo con valores por defecto
      const secciones = [{ id: '__modulo__', nombre: 'Todo el módulo' }, ...(selectedModulo.secciones || [])];
      secciones.forEach(sec => {
        permisos[sec.id] = {
          acciones: {},
          alcance: { tipo: 'grupo', grupos_adicionales_ids: [], incluir_sin_grupo: false }
        };
      });
      
      // Aplicar permisos guardados
      if (res.data && res.data.length > 0) {
        const permisoModulo = res.data.find(p => p.modulo_id === selectedModulo.id);
        if (permisoModulo) {
          permisoModulo.secciones?.forEach(s => {
            // Solo aplicar si la sección existe en el estado actual
            if (permisos[s.seccion_id]) {
              permisos[s.seccion_id] = {
                acciones: s.acciones || {},
                alcance: s.alcance || { tipo: 'grupo' }
              };
            }
          });
        }
      }
      
      setPermisosEditando(permisos);
    } catch (error) {
      console.error('Error loading permisos:', error);
      // Inicializar vacío
      const permisos = {};
      const secciones = [{ id: '__modulo__', nombre: 'Todo el módulo' }, ...(selectedModulo.secciones || [])];
      secciones.forEach(sec => {
        permisos[sec.id] = {
          acciones: {},
          alcance: { tipo: 'grupo' }
        };
      });
      setPermisosEditando(permisos);
    }
  };

  const handleAccionChange = (seccionId, accion, valor) => {
    setPermisosEditando(prev => ({
      ...prev,
      [seccionId]: {
        ...prev[seccionId],
        acciones: {
          ...prev[seccionId]?.acciones,
          [accion]: valor
        }
      }
    }));
  };

  const handleAlcanceChange = (seccionId, tipo) => {
    setPermisosEditando(prev => ({
      ...prev,
      [seccionId]: {
        ...prev[seccionId],
        alcance: {
          ...prev[seccionId]?.alcance,
          tipo
        }
      }
    }));
  };

  const handleToggleAll = (seccionId, valor) => {
    const acciones = {};
    ACCIONES.forEach(a => {
      acciones[a.key] = valor;
    });
    setPermisosEditando(prev => ({
      ...prev,
      [seccionId]: {
        ...prev[seccionId],
        acciones
      }
    }));
  };

  const handleSave = async () => {
    if (!selectedGrupo || !selectedModulo) return;
    
    setSaving(true);
    try {
      // Convertir a formato del API
      const secciones = Object.entries(permisosEditando).map(([seccionId, data]) => ({
        seccion_id: seccionId,
        acciones: data.acciones || {},
        alcance: data.alcance || { tipo: 'grupo' }
      }));
      
      await api.post('/permisos-v2/grupo', {
        grupo_id: selectedGrupo.id,
        modulo_id: selectedModulo.id,
        secciones
      });
      
      toast.success('Permisos guardados correctamente');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Error al guardar permisos');
    } finally {
      setSaving(false);
    }
  };

  const getSecciones = () => {
    if (!selectedModulo) return [];
    return [
      { id: '__modulo__', nombre: 'Todo el módulo', ruta: '' },
      ...(selectedModulo.secciones || [])
    ];
  };

  const countAccionesActivas = (seccionId) => {
    const acciones = permisosEditando[seccionId]?.acciones || {};
    return Object.values(acciones).filter(v => v === true).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="animate-spin h-8 w-8 text-purple-600" />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 bg-slate-50 min-h-screen" data-testid="permisos-config-v2">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} className="mr-1" />
            Volver
          </Button>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Shield className="text-purple-600" />
              Control de Acceso Granular
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Define permisos detallados por grupo, módulo, sección y alcance de datos
            </p>
          </div>
          
          <Button
            onClick={handleSave}
            disabled={saving || !selectedGrupo || !selectedModulo}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
            Guardar Permisos
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Panel izquierdo: Selección */}
        <div className="space-y-4">
          {/* Selector de Grupo */}
          <Card className="p-4 bg-white">
            <Label className="text-sm font-medium text-slate-700 mb-2 block">
              <Users size={14} className="inline mr-1" />
              Grupo
            </Label>
            <Select 
              value={selectedGrupo?.id || ''} 
              onValueChange={(v) => setSelectedGrupo(grupos.find(g => g.id === v))}
            >
              <SelectTrigger className="bg-white">
                <SelectValue placeholder="Seleccionar grupo" />
              </SelectTrigger>
              <SelectContent>
                {grupos.map(g => (
                  <SelectItem key={g.id} value={g.id}>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: g.color || '#3b82f6' }}
                      />
                      {g.nombre}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {selectedGrupo && (
              <div className="mt-3 text-xs text-slate-500">
                <p>{selectedGrupo.member_count || 0} miembros</p>
                {selectedGrupo.supervisores_ids?.length > 0 && (
                  <p className="text-purple-600">
                    {selectedGrupo.supervisores_ids.length} supervisor(es)
                  </p>
                )}
              </div>
            )}
          </Card>

          {/* Selector de Módulo */}
          <Card className="p-4 bg-white">
            <Label className="text-sm font-medium text-slate-700 mb-2 block">
              <Building size={14} className="inline mr-1" />
              Módulo
            </Label>
            <Select 
              value={selectedModulo?.id || ''} 
              onValueChange={(v) => setSelectedModulo(modulos.find(m => m.id === v))}
            >
              <SelectTrigger className="bg-white">
                <SelectValue placeholder="Seleccionar módulo" />
              </SelectTrigger>
              <SelectContent>
                {modulos.map(m => (
                  <SelectItem key={m.id} value={m.id}>{m.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {selectedModulo && (
              <div className="mt-3 text-xs text-slate-500">
                {selectedModulo.secciones?.length || 0} secciones
              </div>
            )}
          </Card>

          {/* Leyenda de Alcance */}
          <Card className="p-4 bg-white">
            <Label className="text-sm font-medium text-slate-700 mb-3 block">
              <Target size={14} className="inline mr-1" />
              Tipos de Alcance
            </Label>
            <div className="space-y-2">
              {TIPOS_ALCANCE.map(t => {
                const Icon = t.icon;
                return (
                  <div key={t.key} className="flex items-start gap-2 text-xs">
                    <Icon size={14} className="text-slate-400 mt-0.5" />
                    <div>
                      <span className="font-medium text-slate-700">{t.label}:</span>
                      <span className="text-slate-500 ml-1">{t.descripcion}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        {/* Panel derecho: Matriz de permisos */}
        <Card className="p-4 bg-white lg:col-span-3">
          {selectedGrupo && selectedModulo ? (
            <>
              <div className="flex items-center justify-between mb-4 pb-4 border-b">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    Permisos: {selectedGrupo.nombre}
                  </h2>
                  <p className="text-sm text-slate-500">
                    Módulo: {selectedModulo.nombre}
                  </p>
                </div>
                
                <Button variant="outline" size="sm" onClick={loadPermisosGrupo}>
                  <RefreshCw size={14} className="mr-1" />
                  Recargar
                </Button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left py-3 px-2 text-sm font-medium text-slate-700 w-48">
                        Sección
                      </th>
                      <th className="text-center py-3 px-2 text-sm font-medium text-slate-700 w-32">
                        Alcance
                      </th>
                      {ACCIONES.map(a => {
                        const Icon = a.icon;
                        return (
                          <th key={a.key} className="text-center py-3 px-1 w-16">
                            <div className="flex flex-col items-center gap-1">
                              <Icon size={14} className={a.color} />
                              <span className="text-xs text-slate-600">{a.label}</span>
                            </div>
                          </th>
                        );
                      })}
                      <th className="text-center py-3 px-2 w-20">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getSecciones().map((seccion, idx) => {
                      const isModulo = seccion.id === '__modulo__';
                      const permisoSeccion = permisosEditando[seccion.id] || { acciones: {}, alcance: { tipo: 'grupo' } };
                      
                      return (
                        <tr 
                          key={seccion.id} 
                          className={`border-b hover:bg-slate-50 ${isModulo ? 'bg-purple-50' : ''}`}
                        >
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-2">
                              {isModulo && <Shield size={14} className="text-purple-600" />}
                              <div>
                                <span className={`text-sm ${isModulo ? 'font-semibold text-purple-900' : 'text-slate-700'}`}>
                                  {seccion.nombre}
                                </span>
                                {seccion.ruta && (
                                  <span className="text-xs text-slate-400 block">{seccion.ruta}</span>
                                )}
                                {notaSeccion(seccion) && (
                                  <span className="mt-1 inline-flex items-start gap-1 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-1 max-w-[260px]" data-testid={`permiso-nota-${seccion.key || 'cobranzas'}`}>
                                    <Trash2 size={11} className="mt-0.5 text-red-500 shrink-0" />
                                    <span>{notaSeccion(seccion)}</span>
                                  </span>
                                )}
                                {(ACCIONES_EXTRA_SECCION[seccion.key] || []).map(x => (
                                  <label key={x.key} className="mt-1 flex cursor-pointer items-center gap-1.5 text-[11px] text-slate-600" data-testid={`permiso-extra-${seccion.key}-${x.key}`}>
                                    <input type="checkbox" checked={!!permisoSeccion.acciones?.[x.key]}
                                      onChange={(e) => handleAccionChange(seccion.id, x.key, e.target.checked)} className="accent-purple-600" />
                                    {x.label}
                                  </label>
                                ))}
                              </div>
                            </div>
                          </td>
                          
                          {/* Alcance */}
                          <td className="py-3 px-2">
                            <Select 
                              value={permisoSeccion.alcance?.tipo || 'grupo'}
                              onValueChange={(v) => handleAlcanceChange(seccion.id, v)}
                            >
                              <SelectTrigger className="h-8 text-xs bg-white w-full">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {TIPOS_ALCANCE.map(t => (
                                  <SelectItem key={t.key} value={t.key}>
                                    <span className="text-xs">{t.label}</span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          
                          {/* Acciones */}
                          {ACCIONES.map(a => {
                            const isChecked = permisoSeccion.acciones?.[a.key] || false;
                            return (
                              <td key={a.key} className="text-center py-3 px-1">
                                <label className="inline-flex items-center justify-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) => handleAccionChange(seccion.id, a.key, e.target.checked)}
                                    className="sr-only peer"
                                  />
                                  <div className={`
                                    w-6 h-6 rounded border-2 flex items-center justify-center transition-all
                                    ${isChecked 
                                      ? 'bg-purple-600 border-purple-600 text-white' 
                                      : 'bg-white border-slate-300 hover:border-purple-400'
                                    }
                                  `}>
                                    {isChecked && <Check size={14} strokeWidth={3} />}
                                  </div>
                                </label>
                              </td>
                            );
                          })}
                          
                          {/* Acciones rápidas */}
                          <td className="text-center py-3 px-2">
                            <div className="flex justify-center gap-1">
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => handleToggleAll(seccion.id, true)}
                                className="h-8 w-8 p-0 bg-green-600 hover:bg-green-700 text-white"
                                title="Dar todos los permisos"
                              >
                                <Check size={16} strokeWidth={3} />
                              </Button>
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => handleToggleAll(seccion.id, false)}
                                className="h-8 w-8 p-0 bg-red-600 hover:bg-red-700 text-white"
                                title="Quitar todos los permisos"
                              >
                                <X size={16} strokeWidth={3} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Resumen */}
              <div className="mt-4 pt-4 border-t flex items-center justify-between">
                <div className="text-sm text-slate-500">
                  {getSecciones().length} secciones configuradas
                </div>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                  Guardar Cambios
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <Shield size={48} className="mx-auto mb-3 opacity-30" />
              <p>Selecciona un grupo y módulo para configurar permisos</p>
            </div>
          )}
        </Card>
      </div>

      {/* Info adicional */}
      <Card className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
        <h3 className="font-medium text-purple-900 mb-2">¿Cómo funcionan los permisos?</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-purple-800">
          <div>
            <p className="font-medium">Jerarquía</p>
            <p className="text-purple-600">Los permisos del módulo aplican como base. Las secciones pueden restringir pero no ampliar.</p>
          </div>
          <div>
            <p className="font-medium">Alcance</p>
            <p className="text-purple-600">Define QUÉ registros puede ver/editar: solo los suyos, los de su grupo, o todos.</p>
          </div>
          <div>
            <p className="font-medium">Supervisores</p>
            <p className="text-purple-600">Con alcance "Grupos supervisados", pueden ver registros de grupos que supervisan.</p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PermisosConfigV2;
